/**
 * CustomerVault — Customer dashboard for managing their own vault items
 * 
 * Features:
 * - List own vault items (customer_id filtered automatically by API)
 * - Create new secret (modal form)
 * - Edit secret metadata + password
 * - Delete secret (soft delete)
 * - Search + pagination (20 items/page)
 * - Dark theme
 * - Responsive (mobile 100%)
 * 
 * APIs:
 * - GET /api/v1/vault/items (own items only)
 * - POST /api/v1/vault/items (create)
 * - PUT /api/v1/vault/items/:id (update)
 * - DELETE /api/v1/vault/items/:id (soft delete)
 */

import { useState, useEffect } from 'react';
import { SkeletonBar } from '../ui';
import { T } from '../../styles/tokens';
import { Button, Textarea } from '../ui';

interface VaultItem {
  id: string;
  customer_id: string;
  name: string;
  description: string | null;
  category: string;
  created_by: string;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

type ModalType = 'create' | 'edit' | null;

interface PageState {
  loading: boolean;
  error: string | null;
  items: VaultItem[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
}

interface FormState {
  loading: boolean;
  error: string | null;
  formData: {
    name: string;
    description: string;
    category: string;
    value: string;
  };
}

const LIMIT = 20;
const CATEGORIES = ['password', 'api_key', 'token', 'certificate', 'credential', 'ssh_key', 'other'];

export function CustomerVault() {
  // ─ Pagination & Search ─
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // ─ Page state ─
  const [pageState, setPageState] = useState<PageState>({
    loading: true,
    error: null,
    items: [],
    totalCount: 0,
    currentPage: 1,
    totalPages: 1,
  });

  // ─ Modal state ─
  const [modalType, setModalType] = useState<ModalType>(null);
  const [selectedItem, setSelectedItem] = useState<VaultItem | null>(null);
  const [formState, setFormState] = useState<FormState>({
    loading: false,
    error: null,
    formData: { name: '', description: '', category: 'password', value: '' },
  });

  // ─ Delete confirmation ─
  const [deleteConfirm, setDeleteConfirm] = useState<{ itemId: string; itemName: string } | null>(null);

  // ─ Fetch items ─
  const fetchItems = async (page = 1) => {
    setPageState((s) => ({ ...s, loading: true, error: null }));
    try {
      const params = new URLSearchParams({
        limit: String(LIMIT),
        page: String(page),
      });
      if (search) params.append('search', search);

      const response = await fetch(`/api/v1/vault/items?${params}`, {
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      const totalPages = Math.ceil(data.total / LIMIT);

      setPageState({
        loading: false,
        error: null,
        items: data.items || [],
        totalCount: data.total || 0,
        currentPage: page,
        totalPages: Math.max(1, totalPages),
      });
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Erro desconhecido';
      setPageState((s) => ({
        ...s,
        loading: false,
        error: errorMsg,
        items: [],
        totalCount: 0,
      }));
    }
  };

  // ─ Open create modal ─
  const handleCreateOpen = () => {
    setSelectedItem(null);
    setFormState({
      loading: false,
      error: null,
      formData: { name: '', description: '', category: 'password', value: '' },
    });
    setModalType('create');
  };

  // ─ Open edit modal ─
  const handleEditOpen = (item: VaultItem) => {
    setSelectedItem(item);
    setFormState({
      loading: false,
      error: null,
      formData: {
        name: item.name,
        description: item.description || '',
        category: item.category,
        value: '',
      },
    });
    setModalType('edit');
  };

  // ─ Submit create/edit ─
  const handleFormSubmit = async () => {
    if (!formState.formData.name.trim()) {
      setFormState((s) => ({ ...s, error: 'Nome é obrigatório' }));
      return;
    }
    if (modalType === 'create' && !formState.formData.value.trim()) {
      setFormState((s) => ({ ...s, error: 'Valor é obrigatório para novos items' }));
      return;
    }

    setFormState((s) => ({ ...s, loading: true, error: null }));
    try {
      if (modalType === 'create') {
        // Create new item
        const response = await fetch('/api/v1/vault/items', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: formState.formData.name,
            description: formState.formData.description || undefined,
            category: formState.formData.category,
            value: formState.formData.value,
          }),
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        setModalType(null);
        await fetchItems(1);
      } else if (modalType === 'edit' && selectedItem) {
        // Update existing item
        const payload: Record<string, unknown> = {
          name: formState.formData.name,
          description: formState.formData.description || undefined,
          category: formState.formData.category,
        };
        if (formState.formData.value.trim()) {
          payload.value = formState.formData.value;
        }

        const response = await fetch(`/api/v1/vault/items/${selectedItem.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        setModalType(null);
        await fetchItems(pageState.currentPage);
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Erro na operação';
      setFormState((s) => ({ ...s, error: errorMsg, loading: false }));
    }
  };

  // ─ Delete item ─
  const handleDelete = async (itemId: string) => {
    try {
      const response = await fetch(`/api/v1/vault/items/${itemId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      setDeleteConfirm(null);
      await fetchItems(pageState.currentPage);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Erro ao deletar';
      alert(`Erro: ${errorMsg}`);
    }
  };

  // ─ Search ─
  const handleSearch = () => {
    setSearch(searchInput);
  };

  const handleSearchKeydown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // ─ Initial load + dependencies ─
  useEffect(() => {
    fetchItems(1);
  }, [search]);

  // ─ Format date ─
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // ─ Render ─
  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
 <h1 style={styles.title}>Meus Secrets</h1>
        <p style={styles.subtitle}>Gerencie seus passwords, tokens e credenciais</p>
      </div>

      {/* Controls */}
      <div style={styles.controls}>
        <div style={styles.searchGroup}>
          <input
            type="text"
            placeholder="Buscar por nome..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={handleSearchKeydown}
            style={styles.input}
          />
          <Button variant="ghost" onClick={handleSearch} style={styles.searchBtn}>
 Buscar
          </Button>
        </div>

        <Button variant="ghost" onClick={handleCreateOpen} style={styles.createBtn}>
 Novo Secret
        </Button>
      </div>

      {/* Error state */}
      {pageState.error && (
        <div style={styles.errorBox}>
 <strong>Erro:</strong> {pageState.error}
        </div>
      )}

      {/* Loading state */}
      {pageState.loading && (
        <div style={styles.loadingBox}>
 <div style={styles.spinner}></div>
          <div style={{ padding: 12 }}><SkeletonBar width="60%" height={14} style={{ marginBottom: 8 }} /><SkeletonBar width="40%" height={14} /></div>
        </div>
      )}

      {/* Table */}
      {!pageState.loading && pageState.items.length > 0 && (
        <>
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr style={styles.headerRow}>
                  <th style={styles.th}>Nome</th>
                  <th style={styles.th}>Tipo</th>
                  <th style={styles.th}>Criado em</th>
                  <th style={styles.th}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {pageState.items.map((item) => (
                  <tr key={item.id} style={styles.row}>
                    <td style={styles.td}>
                      <div>
                        <div style={styles.itemName}>{item.name}</div>
                        {item.description && (
                          <div style={styles.itemDescription}>{item.description}</div>
                        )}
                      </div>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.badge}>{item.category}</span>
                    </td>
                    <td style={styles.td}>
                      {formatDate(item.created_at)}
                    </td>
                    <td style={styles.td}>
                      <Button variant="ghost"
                        onClick={() => handleEditOpen(item)}
                        style={{ ...styles.actionBtn, marginRight: 8 }}
                        title="Editar"
                      >
 
                      </Button>
                      <Button variant="ghost"
                        onClick={() => setDeleteConfirm({ itemId: item.id, itemName: item.name })}
                        style={styles.actionBtnDanger}
                        title="Deletar"
                      >
 
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div style={styles.pagination}>
            <Button variant="ghost"
              onClick={() => fetchItems(pageState.currentPage - 1)}
              disabled={pageState.currentPage === 1}
              style={styles.paginationBtn}
            >
              ← Anterior
            </Button>
            <span style={styles.pageInfo}>
              Página {pageState.currentPage} de {pageState.totalPages} ({pageState.totalCount} secrets)
            </span>
            <Button variant="ghost"
              onClick={() => fetchItems(pageState.currentPage + 1)}
              disabled={pageState.currentPage === pageState.totalPages}
              style={styles.paginationBtn}
            >
              Próxima →
            </Button>
          </div>
        </>
      )}

      {/* Empty state */}
      {!pageState.loading && pageState.items.length === 0 && !pageState.error && (
        <div style={styles.emptyBox}>
 <div style={styles.emptyIcon}></div>
          <p style={styles.emptyText}>Você não tem nenhum secret ainda</p>
          <Button variant="ghost" onClick={handleCreateOpen} style={styles.createBtnSecondary}>
 Criar Primeiro Secret
          </Button>
        </div>
      )}

      {/* Modal: Create */}
      {modalType === 'create' && (
        <FormModal
 title="Novo Secret"
          isEdit={false}
          formData={formState.formData}
          loading={formState.loading}
          error={formState.error}
          onFormChange={(field, value) => {
            setFormState((s) => ({
              ...s,
              formData: { ...s.formData, [field]: value },
            }));
          }}
          onSubmit={handleFormSubmit}
          onClose={() => setModalType(null)}
        />
      )}

      {/* Modal: Edit */}
      {modalType === 'edit' && selectedItem && (
        <FormModal
 title="Editar Secret"
          isEdit={true}
          formData={formState.formData}
          loading={formState.loading}
          error={formState.error}
          onFormChange={(field, value) => {
            setFormState((s) => ({
              ...s,
              formData: { ...s.formData, [field]: value },
            }));
          }}
          onSubmit={handleFormSubmit}
          onClose={() => setModalType(null)}
        />
      )}

      {/* Delete confirmation modal */}
      {deleteConfirm && (
        <DeleteConfirmModal
          itemName={deleteConfirm.itemName}
          loading={false}
          onConfirm={() => handleDelete(deleteConfirm.itemId)}
          onCancel={() => setDeleteConfirm(null)}
        />
      )}
    </div>
  );
}

/**
 * FormModal — Create/Edit secret form
 */
function FormModal({
  title,
  isEdit,
  formData,
  loading,
  error,
  onFormChange,
  onSubmit,
  onClose,
}: {
  title: string;
  isEdit: boolean;
  formData: { name: string; description: string; category: string; value: string };
  loading: boolean;
  error: string | null;
  onFormChange: (field: string, value: string) => void;
  onSubmit: () => void;
  onClose: () => void;
}) {
  return (
    <>
      <div style={styles.overlay} onClick={onClose} />
      <div style={styles.modal}>
        <div style={styles.modalHeader}>
          <h2>{title}</h2>
          <Button variant="ghost" onClick={onClose} style={styles.closeBtn} disabled={loading}></Button>
        </div>

        <div style={styles.modalContent}>
          {error && <div style={styles.errorBox}>{error}</div>}

          <div style={styles.formGroup}>
            <label style={styles.formLabel}>Nome *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => onFormChange('name', e.target.value)}
              style={styles.formInput}
              disabled={loading}
              placeholder="Ex: Database Password"
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.formLabel}>Descrição</label>
            <Textarea
              value={formData.description}
              onChange={(e) => onFormChange('description', e.target.value)}
              style={styles.formTextarea}
              disabled={loading}
              rows={2}
              placeholder="Ex: Production database access"
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.formLabel}>Tipo</label>
            <select
              value={formData.category}
              onChange={(e) => onFormChange('category', e.target.value)}
              style={styles.formSelect}
              disabled={loading}
            >
              {CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.formLabel}>
              Valor {!isEdit && '*'}
              {isEdit && '(deixe em branco para não alterar)'}
            </label>
            <input
              type="password"
              value={formData.value}
              onChange={(e) => onFormChange('value', e.target.value)}
              style={styles.formInput}
              disabled={loading}
              placeholder={isEdit ? 'Novo valor (opcional)' : 'Ex: MyP@ssw0rd!'}
            />
          </div>
        </div>

        <div style={styles.modalFooter}>
          <Button variant="ghost" onClick={onClose} style={styles.modalBtnSecondary} disabled={loading}>
            Cancelar
          </Button>
          <Button variant="ghost" onClick={onSubmit} style={styles.modalBtnPrimary} disabled={loading}>
 {loading ? 'Processando...' : (isEdit ? 'Atualizar' : 'Criar')}
          </Button>
        </div>
      </div>
    </>
  );
}

/**
 * DeleteConfirmModal — Confirm delete
 */
function DeleteConfirmModal({
  itemName,
  loading,
  onConfirm,
  onCancel,
}: {
  itemName: string;
  loading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <>
      <div style={styles.overlay} onClick={onCancel} />
      <div style={styles.modal}>
        <div style={styles.modalHeader}>
 <h2>Confirmar Exclusão</h2>
          <Button variant="ghost" onClick={onCancel} style={styles.closeBtn} disabled={loading}></Button>
        </div>

        <div style={styles.modalContent}>
          <p style={styles.confirmText}>
            Tem certeza que deseja deletar <strong>{itemName}</strong>?
          </p>
          <p style={styles.confirmSmall}>Esta ação não pode ser desfeita.</p>
        </div>

        <div style={styles.modalFooter}>
          <Button variant="ghost" onClick={onCancel} style={styles.modalBtnSecondary} disabled={loading}>
            Cancelar
          </Button>
          <Button variant="ghost" onClick={onConfirm} style={styles.modalBtnDanger} disabled={loading}>
 {loading ? 'Deletando...' : 'Deletar'}
          </Button>
        </div>
      </div>
    </>
  );
}

// ─ Styles ─
const styles = {
  container: {
    padding: '32px 20px',
    maxWidth: '1000px',
    margin: '0 auto',
    fontFamily: 'var(--font)',
    color: 'var(--text)',
  } as React.CSSProperties,

  header: {
    marginBottom: '32px',
  } as React.CSSProperties,

  title: {
    fontSize: '28px',
    fontWeight: 700,
    marginBottom: '8px',
    color: 'var(--text)',
  } as React.CSSProperties,

  subtitle: {
    fontSize: '14px',
    color: 'var(--text-muted)',
  } as React.CSSProperties,

  controls: {
    display: 'flex',
    gap: '12px',
    marginBottom: '24px',
    flexWrap: 'wrap',
    alignItems: 'center',
  } as React.CSSProperties,

  searchGroup: {
    display: 'flex',
    gap: '8px',
    flex: 1,
    minWidth: '280px',
  } as React.CSSProperties,

  input: {
    flex: 1,
    padding: '8px 12px',
    borderRadius: '8px',
    border: '1px solid var(--border)',
    background: 'var(--bg-input)',
    color: 'var(--text)',
    fontSize: '13px',
    fontFamily: 'var(--font)',
    transition: 'border-color 0.2s',
  } as React.CSSProperties,

  searchBtn: {
    padding: '8px 16px',
    borderRadius: '8px',
    border: 'none',
    background: 'var(--primary)',
    color: T.text,
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background 0.2s',
  } as React.CSSProperties,

  createBtn: {
    padding: '8px 16px',
    borderRadius: '8px',
    border: 'none',
    background: 'var(--success)',
    color: T.text,
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background 0.2s',
  } as React.CSSProperties,

  createBtnSecondary: {
    marginTop: '16px',
    padding: '8px 16px',
    borderRadius: '8px',
    border: 'none',
    background: 'var(--primary)',
    color: T.text,
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background 0.2s',
  } as React.CSSProperties,

  errorBox: {
    padding: '12px 16px',
    borderRadius: '8px',
    background: T.dangerBg,
    border: `1px solid ${T.dangerBgStrong}`,
    color: T.danger,
    fontSize: '13px',
    marginBottom: '16px',
  } as React.CSSProperties,

  loadingBox: {
    padding: '48px 20px',
    textAlign: 'center',
    color: 'var(--text-muted)',
  } as React.CSSProperties,

  spinner: {
    fontSize: '32px',
    marginBottom: '12px',
  } as React.CSSProperties,

  tableWrapper: {
    overflowX: 'auto',
    borderRadius: '12px',
    border: '1px solid var(--border)',
    marginBottom: '24px',
  } as React.CSSProperties,

  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '13px',
  } as React.CSSProperties,

  headerRow: {
    background: T.primaryBg,
    borderBottom: '1px solid var(--border)',
  } as React.CSSProperties,

  th: {
    padding: '12px 16px',
    textAlign: 'left',
    fontWeight: 600,
    color: 'var(--text)',
    textTransform: 'uppercase',
    fontSize: '11px',
    letterSpacing: '0.5px',
  } as React.CSSProperties,

  row: {
    borderBottom: '1px solid var(--border)',
    transition: 'background 0.2s',
  } as React.CSSProperties,

  td: {
    padding: '12px 16px',
    color: 'var(--text)',
    verticalAlign: 'middle',
  } as React.CSSProperties,

  itemName: {
    fontWeight: 600,
    marginBottom: '2px',
  } as React.CSSProperties,

  itemDescription: {
    fontSize: '11px',
    color: 'var(--text-muted)',
  } as React.CSSProperties,

  badge: {
    display: 'inline-block',
    padding: '4px 8px',
    borderRadius: '6px',
    background: 'rgba(107, 114, 128, 0.2)',
    color: 'var(--text-muted)',
    fontSize: '11px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  } as React.CSSProperties,

  actionBtn: {
    padding: '4px 8px',
    borderRadius: '6px',
    border: '1px solid var(--border)',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: '13px',
    transition: 'all 0.2s',
    color: 'var(--text)',
  } as React.CSSProperties,

  actionBtnDanger: {
    padding: '4px 8px',
    borderRadius: '6px',
    border: `1px solid ${T.dangerBgStrong}`,
    background: 'transparent',
    cursor: 'pointer',
    fontSize: '13px',
    transition: 'all 0.2s',
    color: T.danger,
  } as React.CSSProperties,

  pagination: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '16px',
    marginTop: '24px',
    padding: '16px',
    borderRadius: '12px',
    background: 'var(--bg-card)',
    border: '1px solid var(--border)',
  } as React.CSSProperties,

  paginationBtn: {
    padding: '8px 16px',
    borderRadius: '8px',
    border: '1px solid var(--border)',
    background: 'transparent',
    color: 'var(--text)',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s',
  } as React.CSSProperties,

  pageInfo: {
    fontSize: '12px',
    color: 'var(--text-muted)',
  } as React.CSSProperties,

  emptyBox: {
    padding: '48px 20px',
    textAlign: 'center',
    borderRadius: '12px',
    border: '1px solid var(--border)',
    background: 'var(--bg-card)',
  } as React.CSSProperties,

  emptyIcon: {
    fontSize: '48px',
    marginBottom: '12px',
  } as React.CSSProperties,

  emptyText: {
    fontSize: '14px',
    color: 'var(--text-muted)',
    marginBottom: '12px',
  } as React.CSSProperties,

  // ─ Modal styles ─
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: T.bgOverlay,
    zIndex: 999,
  } as React.CSSProperties,

  modal: {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: '500px',
    maxHeight: '90vh',
    overflowY: 'auto',
    background: 'var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: '12px',
    zIndex: 1000,
  } as React.CSSProperties,

  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    borderBottom: '1px solid var(--border)',
  } as React.CSSProperties,

  closeBtn: {
    padding: '0',
    width: '32px',
    height: '32px',
    borderRadius: '6px',
    border: 'none',
    background: 'transparent',
    color: 'var(--text-muted)',
    cursor: 'pointer',
    fontSize: '18px',
    transition: 'all 0.2s',
  } as React.CSSProperties,

  modalContent: {
    padding: '20px',
  } as React.CSSProperties,

  formGroup: {
    marginBottom: '16px',
    display: 'flex',
    flexDirection: 'column',
  } as React.CSSProperties,

  formLabel: {
    fontSize: '12px',
    fontWeight: 600,
    color: 'var(--text-muted)',
    marginBottom: '6px',
  } as React.CSSProperties,

  formInput: {
    padding: '8px 12px',
    borderRadius: '8px',
    border: '1px solid var(--border)',
    background: 'var(--bg-input)',
    color: 'var(--text)',
    fontSize: '13px',
    fontFamily: 'var(--font)',
  } as React.CSSProperties,

  formTextarea: {
    padding: '8px 12px',
    borderRadius: '8px',
    border: '1px solid var(--border)',
    background: 'var(--bg-input)',
    color: 'var(--text)',
    fontSize: '13px',
    fontFamily: 'var(--font)',
    resize: 'vertical',
  } as React.CSSProperties,

  formSelect: {
    padding: '8px 12px',
    borderRadius: '8px',
    border: '1px solid var(--border)',
    background: 'var(--bg-input)',
    color: 'var(--text)',
    fontSize: '13px',
    fontFamily: 'var(--font)',
    cursor: 'pointer',
  } as React.CSSProperties,

  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    padding: '16px 20px',
    borderTop: '1px solid var(--border)',
  } as React.CSSProperties,

  modalBtnPrimary: {
    padding: '8px 16px',
    borderRadius: '8px',
    border: 'none',
    background: 'var(--primary)',
    color: T.text,
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 600,
    transition: 'all 0.2s',
  } as React.CSSProperties,

  modalBtnSecondary: {
    padding: '8px 16px',
    borderRadius: '8px',
    border: '1px solid var(--border)',
    background: 'transparent',
    color: 'var(--text)',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 600,
    transition: 'all 0.2s',
  } as React.CSSProperties,

  modalBtnDanger: {
    padding: '8px 16px',
    borderRadius: '8px',
    border: 'none',
    background: T.danger,
    color: T.text,
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 600,
    transition: 'all 0.2s',
  } as React.CSSProperties,

  confirmText: {
    fontSize: '14px',
    color: 'var(--text)',
    marginBottom: '8px',
  } as React.CSSProperties,

  confirmSmall: {
    fontSize: '12px',
    color: 'var(--text-muted)',
  } as React.CSSProperties,
} as const;
